/**
 * StoreMemoryPanel Component
 * Sidebar panel for branch managers to view and manage store memory notes
 */

import React, { useState } from 'react'
import {
  Brain,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Loader2,
  Tag,
  Calendar
} from 'lucide-react'
import { useStoreMemory } from '@/hooks/useStoreMemory'
import {
  StoreMemoryWithCreator,
  StoreMemoryCategory,
  STORE_MEMORY_CATEGORIES
} from '@shared/types'
import { cn } from '@/lib/utils'

interface StoreMemoryPanelProps {
  className?: string
}

const CATEGORY_LABELS: Record<StoreMemoryCategory, string> = {
  priorities: 'Priorities',
  issues: 'Issues',
  goals: 'Goals',
  general: 'General'
}

const CATEGORY_COLORS: Record<StoreMemoryCategory, string> = {
  priorities: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  issues: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  goals: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  general: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
}

export const StoreMemoryPanel: React.FC<StoreMemoryPanelProps> = ({ className }) => {
  const {
    memories,
    loading,
    error,
    activeCount,
    maxNotes,
    canAddMore,
    categories,
    createMemory,
    updateMemory,
    deleteMemory,
    fetchMemories
  } = useStoreMemory()

  const [isExpanded, setIsExpanded] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [newContent, setNewContent] = useState('')
  const [newCategory, setNewCategory] = useState<StoreMemoryCategory>('general')
  const [editContent, setEditContent] = useState('')
  const [editCategory, setEditCategory] = useState<StoreMemoryCategory>('general')
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleAdd = async () => {
    if (!newContent.trim()) return

    setActionLoading(true)
    setActionError(null)

    try {
      await createMemory({
        content: newContent.trim(),
        category: newCategory
      })
      setNewContent('')
      setNewCategory('general')
      setShowAddForm(false)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add note')
    } finally {
      setActionLoading(false)
    }
  }

  const handleEdit = async (id: string) => {
    if (!editContent.trim()) return

    setActionLoading(true)
    setActionError(null)

    try {
      await updateMemory(id, {
        content: editContent.trim(),
        category: editCategory
      })
      setEditingId(null)
      setEditContent('')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update note')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return

    setActionLoading(true)
    setActionError(null)

    try {
      await deleteMemory(id)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete note')
    } finally {
      setActionLoading(false)
    }
  }

  const startEditing = (memory: StoreMemoryWithCreator) => {
    setEditingId(memory.id)
    setEditContent(memory.content)
    setEditCategory(memory.category as StoreMemoryCategory || 'general')
  }

  const cancelEditing = () => {
    setEditingId(null)
    setEditContent('')
    setEditCategory('general')
    setActionError(null)
  }

  const cancelAdding = () => {
    setShowAddForm(false)
    setNewContent('')
    setNewCategory('general')
    setActionError(null)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className={cn("bg-card border rounded-lg", className)}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Brain className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Store Memory</h3>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
            {activeCount}/{maxNotes}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Description */}
          <p className="text-xs text-muted-foreground">
            Notes that the AI assistant remembers for your store
          </p>

          {/* Error Display */}
          {(error || actionError) && (
            <div className="flex items-center space-x-2 text-sm text-destructive bg-destructive/10 p-2 rounded">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{actionError || error}</span>
            </div>
          )}

          {/* Add Button */}
          {!showAddForm && canAddMore && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full flex items-center justify-center space-x-2 py-2 text-sm border border-dashed rounded-md hover:bg-muted/50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Note</span>
            </button>
          )}

          {/* Add Form */}
          {showAddForm && (
            <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Enter a note for the AI to remember..."
                className="w-full px-3 py-2 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                rows={3}
                autoFocus
              />
              <div className="flex items-center space-x-2">
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as StoreMemoryCategory)}
                  className="flex-1 px-2 py-1.5 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {CATEGORY_LABELS[cat]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={cancelAdding}
                  className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!newContent.trim() || actionLoading}
                  className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Add'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Limit Warning */}
          {!canAddMore && (
            <div className="flex items-center space-x-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>Maximum notes reached. Delete notes to add more.</span>
            </div>
          )}

          {/* Notes List */}
          <div className="space-y-2">
            {loading && memories.length === 0 ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : memories.length === 0 ? (
              <div className="text-center py-4 text-sm text-muted-foreground">
                No notes yet. Add one to help the AI remember important context.
              </div>
            ) : (
              memories.map(memory => (
                <div
                  key={memory.id}
                  className={cn(
                    "p-3 rounded-lg border bg-background",
                    editingId === memory.id && "ring-2 ring-primary"
                  )}
                >
                  {editingId === memory.id ? (
                    // Edit Mode
                    <div className="space-y-2">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-muted border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex items-center space-x-2">
                        <select
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value as StoreMemoryCategory)}
                          className="flex-1 px-2 py-1.5 text-sm bg-muted border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {categories.map(cat => (
                            <option key={cat} value={cat}>
                              {CATEGORY_LABELS[cat]}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={cancelEditing}
                          className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
                          disabled={actionLoading}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleEdit(memory.id)}
                          disabled={!editContent.trim() || actionLoading}
                          className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                        >
                          {actionLoading ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display Mode
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm flex-1">{memory.content}</p>
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          <button
                            onClick={() => startEditing(memory)}
                            className="p-1 text-muted-foreground hover:text-foreground rounded"
                            title="Edit"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(memory.id)}
                            className="p-1 text-muted-foreground hover:text-destructive rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        {memory.category && (
                          <span className={cn(
                            "inline-flex items-center space-x-1 px-2 py-0.5 rounded text-xs",
                            CATEGORY_COLORS[memory.category as StoreMemoryCategory]
                          )}>
                            <Tag className="w-3 h-3" />
                            <span>{CATEGORY_LABELS[memory.category as StoreMemoryCategory]}</span>
                          </span>
                        )}
                        <span className="flex items-center space-x-1 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(memory.updatedAt || memory.createdAt)}</span>
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchMemories}
            disabled={loading}
            className="w-full py-2 text-xs text-muted-foreground hover:text-foreground border border-dashed rounded-md hover:bg-muted/50 transition-colors disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh Notes'}
          </button>
        </div>
      )}
    </div>
  )
}

export default StoreMemoryPanel
